import { BigNumber } from '@ethersproject/bignumber'
import { AddressZero } from '@ethersproject/constants'
import { useMemo } from 'react'
import { areEqualAddresses } from 'utils/areEqualAddresses'

/**
 * Enumeration representing the staking state of a position
 */
export enum PositionStakingState {
  NOT_DEPOSITED = 'NOT_DEPOSITED',
  DEPOSITED = 'DEPOSITED',
  STAKED = 'STAKED',
  STAKED_IN_OTHER = 'STAKED_IN_OTHER',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Determines the staking state of a position based on its parameters
 * @param owner The owner of the position
 * @param stakingLiquidity The staked liquidity amount
 * @param numberOfStakes The number of stakes
 * @returns The staking state of the position
 */
export function usePositionStakingState(
  owner: string | undefined,
  stakingLiquidity: BigNumber | undefined,
  numberOfStakes: number | undefined
): PositionStakingState {
  return useMemo(() => {
    if (owner === undefined || stakingLiquidity === undefined || numberOfStakes === undefined) {
      return PositionStakingState.NOT_DEPOSITED
    }

    if (stakingLiquidity.gt(0)) {
      return PositionStakingState.STAKED
    }

    if (numberOfStakes > 0) {
      return PositionStakingState.STAKED_IN_OTHER
    }

    if (!areEqualAddresses(owner, AddressZero)) {
      return PositionStakingState.DEPOSITED
    }

    return PositionStakingState.NOT_DEPOSITED
  }, [owner, stakingLiquidity, numberOfStakes])
}
